import { useContext, useEffect, useState } from "react";
import { Form, Input, Modal, Radio, Space, Tag, Typography } from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CustomerServiceOutlined,
    GlobalOutlined,
    MobileOutlined,
    UserOutlined,
} from "@ant-design/icons";

import { ISupport } from "../../interfaces";
import { ThemeContext } from "../../../../contexts/ThemeContext";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";

const { Text } = Typography;
const { TextArea } = Input;

interface IProps {
    open: boolean;
    support: ISupport | null;
    onClose: () => void;
    onSave: (status: string, observation: string) => Promise<void>;
    isSaving: boolean;
}

export default function ModalUpdateSupport({
    open,
    support,
    onClose,
    onSave,
    isSaving,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [status, setStatus] = useState<string>("Respondido");
    const [observation, setObservation] = useState<string>("");

    useEffect(() => {
        if (open && support) {
            setStatus(support.status || "Respondido");
            setObservation(support.observation || "");
        }
    }, [open, support]);

    const handleOk = async () => {
        await onSave(status, observation);
    };

    const isSite = support?.origin === "Site";

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleOk}
            confirmLoading={isSaving}
            okText="Salvar Atendimento"
            cancelText="Cancelar"
            okButtonProps={{ loading: isSaving }}
            cancelButtonProps={{ disabled: isSaving }}
            centered
            width={580}
            title={
                <Space align="center">
                    <CustomerServiceOutlined style={{ color: "#1677ff", fontSize: 18 }} />
                    <span style={{ fontWeight: 600 }}>Atualizar Atendimento de Suporte</span>
                </Space>
            }
        >
            <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 8, paddingBottom: 6 }}>
                {/* Solicitante Summary Box */}
                {support && (
                    <div
                        style={{
                            background: isDark ? "#141414" : "#f8fafc",
                            border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                            borderRadius: 8,
                            padding: "12px 14px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <UserOutlined style={{ color: "#1677ff", fontSize: 14 }} />
                                <span style={{ fontWeight: 600, color: isDark ? "#f0f0f0" : "#1e293b", fontSize: 13.5 }}>
                                    {support.name || "Solicitante"}
                                </span>
                            </div>
                            <Space size={4}>
                                {isSite ? (
                                    <Tag color="blue" icon={<GlobalOutlined />} style={{ margin: 0, fontSize: 11 }}>
                                        Site
                                    </Tag>
                                ) : (
                                    <Tag color="orange" icon={<MobileOutlined />} style={{ margin: 0, fontSize: 11 }}>
                                        App
                                    </Tag>
                                )}
                                {support.created_at && (
                                    <span style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                        {formatDateStringToDateAndTime(support.created_at)}
                                    </span>
                                )}
                            </Space>
                        </div>
                        {support.message && (
                            <div
                                style={{
                                    fontSize: 12,
                                    color: isDark ? "#aaa" : "#64748b",
                                    maxHeight: 60,
                                    overflowY: "auto",
                                    lineHeight: 1.4,
                                }}
                            >
                                <strong>Mensagem:</strong> {support.message}
                            </div>
                        )}
                    </div>
                )}

                {/* Form Fields */}
                <Form layout="vertical" style={{ marginTop: 4 }}>
                    <Form.Item
                        label={<span style={{ fontWeight: 600, fontSize: 13 }}>Status do Atendimento</span>}
                        required
                        style={{ marginBottom: 16 }}
                    >
                        <Radio.Group
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            buttonStyle="solid"
                            style={{ width: "100%", display: "flex" }}
                        >
                            <Radio.Button
                                value="Aberto"
                                style={{ flex: 1, textAlign: "center" }}
                            >
                                <ClockCircleOutlined style={{ marginRight: 4 }} /> Aberto
                            </Radio.Button>
                            <Radio.Button
                                value="Respondido"
                                style={{
                                    flex: 1,
                                    textAlign: "center",
                                    backgroundColor: status === "Respondido" ? "#1677ff" : undefined,
                                    borderColor: status === "Respondido" ? "#1677ff" : undefined,
                                    color: status === "Respondido" ? "#fff" : undefined,
                                }}
                            >
                                <CheckCircleOutlined style={{ marginRight: 4 }} /> Respondido
                            </Radio.Button>
                            <Radio.Button
                                value="Fechado"
                                style={{
                                    flex: 1,
                                    textAlign: "center",
                                    backgroundColor: status === "Fechado" || status === "CLOSED" ? "#52c41a" : undefined,
                                    borderColor: status === "Fechado" || status === "CLOSED" ? "#52c41a" : undefined,
                                    color: status === "Fechado" || status === "CLOSED" ? "#fff" : undefined,
                                }}
                            >
                                <CheckCircleOutlined style={{ marginRight: 4 }} /> Fechado
                            </Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        label={
                            <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
                                <span style={{ fontWeight: 600, fontSize: 13 }}>Observação Interna</span>
                                <Text type="secondary" style={{ fontSize: 11.5, fontWeight: 400 }}>
                                    (Opcional - visível aos atendentes do painel)
                                </Text>
                            </div>
                        }
                        style={{ marginBottom: 12 }}
                    >
                        <TextArea
                            rows={4}
                            value={observation}
                            onChange={(e) => setObservation(e.target.value)}
                            placeholder="Descreva as ações realizadas no atendimento (ex: Entrei em contato com o munícipe pelo WhatsApp e passei as orientações, solicitação resolvida, etc.)"
                            maxLength={1000}
                            showCount
                            style={{ borderRadius: 6, resize: "vertical" }}
                        />
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
}
